import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  addDays,
  getMonthGridDays,
  isSameDay,
  toYmd,
} from "../../features/calendar/utils/calendar-range";
import { DayOfWeek } from "../../features/instructor-preferences/contracts/DayOfWeek";
import { selectPortalAbsenceOverview, selectPortalMe } from "../../features/portal/portal-slice";
import { fetchPortalAbsenceOverview } from "../../features/portal/slice-extensions/portal-slice-extension";

const MAX_NAMES_PER_CELL = 2;

/** Shared availability: a month grid of who is away when (first names only). */
const PortalAbsenceCalendarCard = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const entries = useAppSelector(selectPortalAbsenceOverview);
  const weekStart = useAppSelector(selectPortalMe)?.organization.weekStart === DayOfWeek.Sunday
    ? "Sunday"
    : "Monday";
  const [anchor, setAnchor] = useState(() => new Date());

  const days = useMemo(() => getMonthGridDays(anchor, weekStart), [anchor, weekStart]);
  const range = useMemo(
    () => ({ from: toYmd(days[0]), to: toYmd(days[days.length - 1]) }),
    [days],
  );

  useEffect(() => {
    dispatch(fetchPortalAbsenceOverview(range));
  }, [dispatch, range]);

  const namesByDate = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const entry of entries) {
      let cursor = new Date(`${entry.startDate}T00:00:00`);
      const end = new Date(`${entry.endDate}T00:00:00`);
      while (cursor <= end) {
        const key = toYmd(cursor);
        map.set(key, [...(map.get(key) ?? []), entry.memberFirstName]);
        cursor = addDays(cursor, 1);
      }
    }
    return map;
  }, [entries]);

  const today = new Date();
  const monthLabel = new Intl.DateTimeFormat(i18n.language, {
    month: "long",
    year: "numeric",
  }).format(anchor);
  const weekdayFormat = new Intl.DateTimeFormat(i18n.language, { weekday: "narrow" });

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-grey-20 dark:text-white">
          {t("portal.absenceCalendar.title", "Who is away")}
        </h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))}
            aria-label={t("portal.absenceCalendar.previousMonth", "Previous month")}
          >
            <ChevronLeft size={14} />
          </button>
          <span className="min-w-28 text-center text-sm text-grey-20 dark:text-white">
            {monthLabel}
          </span>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))}
            aria-label={t("portal.absenceCalendar.nextMonth", "Next month")}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-7 gap-px text-center">
        {days.slice(0, 7).map((day) => (
          <span key={toYmd(day)} className="text-[10px] uppercase text-grey-40 dark:text-grey-80">
            {weekdayFormat.format(day)}
          </span>
        ))}
        {days.map((day) => {
          const key = toYmd(day);
          const names = namesByDate.get(key) ?? [];
          const inMonth = day.getMonth() === anchor.getMonth();
          return (
            <div
              key={key}
              className={`min-h-10 rounded p-0.5 text-left ${inMonth ? "" : "opacity-40"} ${
                names.length > 0 ? "bg-grey-95 dark:bg-primary-20" : ""
              }`}
            >
              <span
                className={`text-[10px] ${
                  isSameDay(day, today)
                    ? "font-bold text-primary-40 dark:text-primary-90"
                    : "text-grey-40 dark:text-grey-80"
                }`}
              >
                {day.getDate()}
              </span>
              {names.slice(0, MAX_NAMES_PER_CELL).map((name, index) => (
                <p key={`${key}-${index}`} className="truncate text-[9px] text-grey-20 dark:text-white">
                  {name}
                </p>
              ))}
              {names.length > MAX_NAMES_PER_CELL && (
                <p className="text-[9px] text-grey-40 dark:text-grey-80">
                  +{names.length - MAX_NAMES_PER_CELL}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PortalAbsenceCalendarCard;
